/**
 * Jahresabschluss provenance (BDA-6154 / BDA-6288) — the pure derivation behind
 * the clinic browser's "this is only a share" surface.
 *
 * Roughly half of all clinic-years that show a Jahresabschluss show a SHARE of
 * an account filed by a legal entity spanning several Standorte. A reader saw
 * 51 M€ where the published account says 244 M€ with nothing on screen saying
 * why; `provenance` on `GET /clinics/{clinicSn}/annual-account` is what the
 * backend added to explain it, and this module turns that wire object into the
 * handful of things a view actually renders.
 *
 * Framework-independent, like everything else in this lib: the component does
 * formatting and layout, this file does the deciding.
 *
 * ## The three rules the wire imposes
 *
 * 1. **`apportioned` is the single discriminator.** The unambiguous case
 *    answers with the same object at `apportioned: false` / `locationCount: 1`
 *    / `share: 1.0`, because naming the legal entity is useful there too. Never
 *    infer the split from counts or shares.
 * 2. **`apportionment` can be absent on an apportioned response.** Where the
 *    stored shares no longer reproduce what the named key would produce, the
 *    backend suppresses the arithmetic rather than show one it cannot stand
 *    behind. The view must be able to say "this is a share of X" without also
 *    saying "and here is how X was divided".
 * 3. **`inputs` is a list, not a numerator/denominator pair.** The key is
 *    expected to change (Betten instead of vollstationäre Fälle), and a method
 *    with one or three inputs must not need a contract change.
 */

import type {
  AysClinicAnnualAccountApportionmentInput,
  AysClinicAnnualAccountMetric,
  AysClinicAnnualAccountProvenance,
} from './clinic-content-sections.model';
import {
  buildAnnualAccountStatement,
  stripAnnualAccountUnitSuffix,
  type AysAnnualAccountStatement,
  type AysAnnualAccountValues,
} from './annual-account-statement.model';

/**
 * Catalog key `provenance.locations[].assignedAmount` reports by default.
 *
 * `g1` — "Erlöse aus Krankenhausleistungen". The endpoint takes a `?metric=`
 * override, but which metric belongs in that column is an open QA question, so
 * the browser ships the backend's default and names the column from the wire's
 * own label rather than asserting one of its own.
 */
export const AYS_ANNUAL_ACCOUNT_ASSIGNED_AMOUNT_KEY = 'g1';

/** Fallback header for the assigned-amount column when the wire names no label. */
const ASSIGNED_AMOUNT_FALLBACK_LABEL = 'Zugeordneter Betrag';

/** One Standort of the split, as the calculation table renders it. */
export interface AysAnnualAccountProvenanceLocation {
  clinicSn: string;
  /** The registry name, falling back to the Standortnummer itself. */
  name: string;
  /**
   * This Standort's value of the distribution key.
   *
   * `null` is "we do not know" — a Standort with no row in the key's source —
   * and is NOT a reported zero. Rare, and always on a sibling whose stored
   * share is itself 0.
   */
  keyValue: number | null;
  /** Fraction in `[0, 1]`. */
  share: number;
  /**
   * This Standort's own stored amount for the reference metric — never
   * `share × total` recomputed. `null` when it was not reported.
   */
  assignedAmount: number | null;
  /** A registry entry is held, so the row may link to that Standort's page. */
  browsable: boolean;
  /** The Standort currently being viewed. */
  self: boolean;
}

/** The arithmetic behind the Standortanteil, when the backend stands behind it. */
export interface AysAnnualAccountProvenanceCalculation {
  /** German name of the distribution key, e.g. "Vollstationäre Fälle". */
  label: string;
  /** Rendered as a list of N rows — never destructured into a pair. */
  inputs: readonly AysClinicAnnualAccountApportionmentInput[];
}

/** Everything the provenance surface renders, derived in one pass. */
export interface AysAnnualAccountProvenanceView {
  /**
   * The figures on screen are a share of a larger filing. The ONLY thing that
   * decides whether the page needs a notice.
   */
  apportioned: boolean;
  /** This Standort's share, as a fraction in `[0, 1]`. */
  share: number;
  /** True for a Standort the key assigned none of the group's filing. */
  zeroShare: boolean;
  /** How many Standorte the filed account covers. */
  locationCount: number;
  /** The filing's own name. */
  accountName: string;
  /** The legal entity, when it is named and differs from {@link accountName}. */
  accountEntity: string | null;
  /** German label for the filing kind ("Einzelabschluss"), when named. */
  accountKindLabel: string | null;
  /** The Träger, when named. */
  accountOwner: string | null;
  accountCompanyId: string | null;
  accountClinicIk: number;
  /** The year the filing is for. */
  accountYear: number | null;
  /** `null` when the backend suppressed an explanation it cannot stand behind. */
  calculation: AysAnnualAccountProvenanceCalculation | null;
  /** Every Standort of the split, in wire order — rows are never dropped. */
  locations: readonly AysAnnualAccountProvenanceLocation[];
  /** Header for the assigned-amount column, taken from the wire's own label. */
  assignedAmountLabel: string;
  /**
   * The legal entity's whole filed statement, unsplit — what the
   * Standortanteil ⇄ Gesamtabschluss toggle re-renders from, and the reference
   * every position of the Standort's own statement is measured against.
   */
  entityStatement: AysAnnualAccountStatement;
  /** The entity's raw values, to pass as `buildAnnualAccountStatement`'s reference. */
  entityValues: AysAnnualAccountValues;
}

/** `key -> value`, with an unreported position kept as an explicit `null`. */
export function annualAccountValues(
  metrics: readonly AysClinicAnnualAccountMetric[],
): AysAnnualAccountValues {
  return Object.fromEntries(
    metrics.map((metric) => [metric.key, metric.value ?? null]),
  );
}

/**
 * `key -> label` from the wire.
 *
 * The backend's curated labels win over the catalog's — the catalog exists so
 * the Virtual Clinic (whose wire carries no labels at all) can render the same
 * statement, not to second-guess the API. The redundant "(EUR)" suffix every
 * label carries is dropped; the unit is stated once, in the column header.
 */
export function annualAccountLabels(
  metrics: readonly AysClinicAnnualAccountMetric[],
): Record<string, string> {
  return Object.fromEntries(
    metrics.map((metric) => [
      metric.key,
      stripAnnualAccountUnitSuffix(metric.label),
    ]),
  );
}

/**
 * Turns the wire's `provenance` into the view model, or `null` when there is
 * nothing to explain.
 *
 * A `null` return means "no explanation available" — which happens both when
 * the clinic filed nothing and, transiently, on every apportioned row between
 * an API deploy and the reprojection that follows it. It must NEVER be read as
 * "no Jahresabschluss": the section's `metrics` is what answers that.
 */
export function buildAnnualAccountProvenanceView(
  provenance: AysClinicAnnualAccountProvenance | null | undefined,
): AysAnnualAccountProvenanceView | null {
  if (provenance == null) return null;

  const account = provenance.account;
  const entityValues = annualAccountValues(account.metrics);
  const entityLabels = annualAccountLabels(account.metrics);
  const entity = account.entity?.trim() || null;

  return {
    apportioned: provenance.apportioned,
    share: provenance.share,
    // Read off the share itself, not off `sharePercent`, which the backend has
    // already rounded — a 0,001 % share must not present as "assigned nothing".
    zeroShare: provenance.apportioned && provenance.share === 0,
    locationCount: provenance.locationCount,
    accountName: account.name,
    // Repeating the filing's own name as its "entity" tells the reader nothing.
    accountEntity: entity === account.name ? null : entity,
    accountKindLabel: account.kindLabel?.trim() || null,
    accountOwner: account.owner?.trim() || null,
    accountCompanyId: account.companyId?.trim() || null,
    accountClinicIk: account.clinicIk,
    accountYear: toYear(account.year),
    calculation: toCalculation(provenance),
    locations: provenance.locations.map((location) => ({
      clinicSn: location.clinicSn,
      name: location.name?.trim() || location.clinicSn,
      keyValue: location.keyValue ?? null,
      share: location.share,
      assignedAmount: location.assignedAmount ?? null,
      browsable: location.browsable,
      self: location.self,
    })),
    assignedAmountLabel:
      entityLabels[AYS_ANNUAL_ACCOUNT_ASSIGNED_AMOUNT_KEY] ||
      ASSIGNED_AMOUNT_FALLBACK_LABEL,
    entityStatement: buildAnnualAccountStatement(entityValues, entityLabels),
    entityValues,
  };
}

/**
 * The arithmetic block, or `null` where there is none to show.
 *
 * Suppressed on purpose by the backend when the stored shares no longer match
 * the named key; also absent on every unambiguous response, where there was no
 * division to explain. An empty `inputs` list is treated the same way — a
 * method named with nothing to show is not an explanation.
 */
function toCalculation(
  provenance: AysClinicAnnualAccountProvenance,
): AysAnnualAccountProvenanceCalculation | null {
  const apportionment = provenance.apportionment;
  if (apportionment == null || apportionment.inputs.length === 0) return null;
  return { label: apportionment.label, inputs: apportionment.inputs };
}

/** The API sends years as strings; everything downstream wants a number. */
function toYear(value: string | undefined): number | null {
  if (value == null) return null;
  const year = Number(value);
  return Number.isFinite(year) ? year : null;
}
